import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { CpSetupModel } from '../../helpers/objects/dto/setup/cp-setup-model';
import { map, Observable, of, tap } from 'rxjs';
import { APP_CONFIG, EnvironmentModel } from '@fh-client-interaction-frontends/fh-app-config';
import { SetupVirtualCardModel } from '../../helpers/objects/dto/setup-virtual-card/setup-virtual-card-model';
import { CpContractsService } from '../contracts/cp-contracts.service';
import { CpSetupConsultModel } from '../../helpers/objects/dto/setup-consult/cp-setup-consult-model';
import { CpGeolocationModel } from '../../helpers/objects/dto/geolocation/cp-geolocation-model';
import { CpGeolocationRequestModel } from '../../helpers/objects/request/geolocation/cp-geolocation-request-model';
import { CpSetupAuthorizationsModel } from '../../helpers/objects/dto/setup-authorizations/cp-setup-authorizations-model';
import { CpMimetypesModel } from '../../helpers/objects/dto/mimetypes/cp-mimetypes-model';
import { CpSetupRefundsModel } from '../../helpers/objects/dto/setup-refunds/cp-setup-refunds-model';
import { CpClientDataEditModel } from '../../helpers/objects/dto/client-data-edit/cp-client-data-edit-model';
import { CountriesModel } from '@fh-client-interaction-frontends/fh-providers';

@Injectable({
  providedIn: 'root'
})
export class CpSetupService {
  appConfig: EnvironmentModel = new EnvironmentModel();

  loginSetup?: CpSetupModel;
  consultSetup?: CpSetupConsultModel;
  authorizationSetup?: CpSetupAuthorizationsModel;
  refundsSetup?: CpSetupRefundsModel;
  filesSetup?: CpMimetypesModel[];
  clientDataEditSetup?: CpClientDataEditModel;

  listCountries: CountriesModel[] = new Array<CountriesModel>();

  constructor(@Inject(APP_CONFIG) protected appConfigTemp: EnvironmentModel, private cpContractsService: CpContractsService, private http: HttpClient) {
    this.appConfig = appConfigTemp;
  }

  login() {
    if (this.loginSetup) {
      return of(this.loginSetup);
    } else {
      this.cpContractsService.contract = undefined;
      this.cpContractsService.listContracts = undefined;

      return this.http.get<any>(this.appConfig.api + 'login/setup').pipe(
        map((response) => response.body as CpSetupModel),
        tap((response) => (this.loginSetup = response))
      );
    }
  }

  virtualCard(token: string): Observable<SetupVirtualCardModel[]> {
    return this.http
      .get<any>(this.appConfig.api + 'contracts/' + encodeURIComponent(token) + '/virtual-card-setup')
      .pipe(map((response) => response.body.persons as SetupVirtualCardModel[]));
  }

  consult(countryId?: number): Observable<CpSetupConsultModel> {
    if (!countryId && this.consultSetup) {
      return of(this.consultSetup);
    } else {
      let url: string = this.appConfig.api + 'appointments/setup';

      if (countryId) {
        url += '?CountryId=' + countryId;
      }

      return this.http.get<any>(url).pipe(
        map((response) => response.body as CpSetupConsultModel),
        tap((response) => (this.consultSetup = response))
      );
    }
  }

  location(location: CpGeolocationRequestModel): Observable<CpGeolocationModel> {
    return this.http.post<any>(this.appConfig.api + 'appointments/setup/location', location).pipe(map((response) => response.body as CpGeolocationModel));
  }

  authorizations(serviceId?: number): Observable<CpSetupAuthorizationsModel> {
    if (!serviceId && this.authorizationSetup) {
      return of(this.authorizationSetup);
    }

    const contract = atob(sessionStorage.getItem('contract')!);
    let url = this.appConfig.api + 'contracts/' + encodeURIComponent(contract) + '/authorizations-setup';

    if (serviceId) {
      url += '?ServiceId=' + serviceId;
    }

    return this.http.get<any>(url).pipe(
      map((response) => response.body as CpSetupAuthorizationsModel),
      tap((response) => {
        if (!serviceId) {
          this.authorizationSetup = response;
        }
      })
    );
  }

  refunds(serviceId?: number): Observable<CpSetupRefundsModel> {
    if (!serviceId && this.refundsSetup) {
      return of(this.refundsSetup);
    }

    const contract = atob(sessionStorage.getItem('contract')!);
    let url = this.appConfig.api + 'contracts/' + encodeURIComponent(contract) + '/refunds-requests/setup';

    if (serviceId) {
      url += '?ServiceId=' + serviceId;
    }

    return this.http.get<any>(url).pipe(
      map((response) => response.body as CpSetupRefundsModel),
      tap((response) => {
        if (!serviceId) {
          this.refundsSetup = response;
        }
      })
    );
  }

  files(): Observable<CpMimetypesModel[]> {
    if (this.filesSetup) {
      return of(this.filesSetup);
    } else {
      return this.http.get<any>(this.appConfig.api + 'files/setup').pipe(
        map((response) => response.body.mimetypes as CpMimetypesModel[]),
        tap((response) => (this.filesSetup = response))
      );
    }
  }

  clientDataEdit(): Observable<CpClientDataEditModel> {
    if (this.clientDataEditSetup) {
      return of(this.clientDataEditSetup);
    } else {
      return this.http.get<any>(this.appConfig.api + 'setup/client-data-edit').pipe(
        map((response) => response.body as CpClientDataEditModel),
        tap((response) => (this.clientDataEditSetup = response))
      );
    }
  }

  countryInfo(): Observable<CountriesModel[]> {
    if (this.listCountries.length > 0) {
      return of(this.listCountries);
    } else {
      return this.http.get<any>(this.appConfig.api + 'country-info').pipe(
        map((response) => response.body.countries as CountriesModel[]),
        tap((response) => (this.listCountries = response))
      );
    }
  }
}
